/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getHygraphCosmosPageData, resolveOwnerSlug } from "@cosmos/hygraph-data";
import { CosmosPageEulaKeyMetadataQuery, Locale } from "@cosmos/hygraph-schema";
import { getHygraphLocale, getSlugParam } from "@cosmos/next-common";
import { isError } from "@epic-mw/error-types";
import { Metadata } from "next";

import { CosmosPageMetadata } from "../common/page.types";

const getPageMetadata = async (
	locale: string = Locale.En,
	slug: string,
	previewParam?: string | string[],
	ownerId?: string,
	endpoint?: string,
): Promise<CosmosPageEulaKeyMetadataQuery> => {
	if (!slug) {
		throw new Error("Missing slug");
	}
	const preview = previewParam && typeof previewParam === "string" ? previewParam : undefined;

	// Transform URL path to Hygraph slug by applying owner prefix
	const hygraphSlug = await resolveOwnerSlug(slug, preview, ownerId, endpoint);

	const pageData = await getHygraphCosmosPageData(
		"CosmosPageEulaKeyMetadata",
		{
			locale: [locale, Locale.En] as Locale[],
			slug: hygraphSlug,
		},
		preview,
		endpoint,
	);

	return pageData;
};

export async function cosmosPageEulaKeyMetadata({
	slug: rootSlug = "",
	params,
	searchParams,
	hygraphLocales,
	getAppMetadata,
	hygraphEndpoint,
	hygraphOwnerId,
}: CosmosPageMetadata): Promise<Metadata> {
	const slugParam = getSlugParam(params);
	const slug = `${rootSlug}${slugParam}`;
	const locale = params.lang;
	let pageData: CosmosPageEulaKeyMetadataQuery = {};
	try {
		pageData = await getPageMetadata(
			getHygraphLocale(hygraphLocales, locale),
			slug,
			searchParams.preview,
			hygraphOwnerId,
			hygraphEndpoint,
		);
	} catch (e) {
		console.error("Failed to get cosmosPageEulaKeyMetadata()", { error: isError(e) && e.message });
	}
	const metaTags = pageData.cosmosPageEulaKey?.metaTags || {};

	return getAppMetadata(metaTags, slug, locale);
}
